import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Send,
  Mail,
  Phone,
  CheckCircle2,
  AlertTriangle,
  FileText,
  User,
  Briefcase,
  Calendar,
  Link2,
  Loader2,
  Plus,
} from "lucide-react";
import PremiumModal from "../../ui/PremiumModal";
import { useBuilder } from "./BuilderContext";
import { invoicesApi, clientsApi } from "../../../services/api";

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: () => Promise<boolean>;
  onComplete: () => void;
}

const formatCurrency = (cents: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "Not set";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const IssueAndSendModal: React.FC<Props> = ({
  open,
  onClose,
  onSave,
  onComplete,
}) => {
  const { state } = useBuilder();

  // Local state
  const [step, setStep] = useState<"review" | "sending" | "success">("review");
  const [error, setError] = useState<string | null>(null);

  // Inline email form
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [localClientEmails, setLocalClientEmails] = useState<
    Array<{ label: string; email: string }>
  >([]);

  // Inline phone form
  const [showPhoneForm, setShowPhoneForm] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [localClientPhones, setLocalClientPhones] = useState<
    Array<{ label: string; number: string }>
  >([]);

  // Derive email and phone from client data + local overrides
  const clientEmails = useMemo(() => {
    if (localClientEmails.length > 0) return localClientEmails;
    return state.client?.emails || [];
  }, [state.client?.emails, localClientEmails]);

  const clientPhones = useMemo(() => {
    if (localClientPhones.length > 0) return localClientPhones;
    return state.client?.phones || [];
  }, [state.client?.phones, localClientPhones]);

  const recipientEmail = useMemo(() => {
    const main = clientEmails.find(
      (e: { label: string; email: string }) => e.label === "main"
    );
    return main?.email || clientEmails[0]?.email || null;
  }, [clientEmails]);

  const recipientPhone = useMemo(() => {
    return clientPhones[0]?.number || null;
  }, [clientPhones]);

  const hasEmail = !!recipientEmail;
  const hasPhone = clientPhones.length > 0;

  const paymentUrl = state.client?._id
    ? `${window.location.origin}/pay/${state.documentId}`
    : null;

  // Email validation
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Save email to client
  const handleSaveEmail = async () => {
    setEmailError("");
    if (!newEmail.trim()) {
      setEmailError("Email is required");
      return;
    }
    if (!validateEmail(newEmail.trim())) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setSavingEmail(true);
    try {
      const existingEmails = state.client?.emails || [];
      const updatedEmails = [
        ...existingEmails,
        { label: "main", email: newEmail.trim() },
      ];
      await clientsApi.update(state.client._id, { emails: updatedEmails });
      setLocalClientEmails(updatedEmails);
      setShowEmailForm(false);
      setNewEmail("");
    } catch (err: any) {
      setEmailError(
        err.response?.data?.error || "Failed to save email. Please try again."
      );
    } finally {
      setSavingEmail(false);
    }
  };

  // Save phone to client
  const handleSavePhone = async () => {
    setPhoneError("");
    if (!newPhone.trim()) {
      setPhoneError("Phone number is required");
      return;
    }
    if (newPhone.trim().length < 7) {
      setPhoneError("Please enter a valid phone number");
      return;
    }
    setSavingPhone(true);
    try {
      const existingPhones = state.client?.phones || [];
      const updatedPhones = [
        ...existingPhones,
        { label: "main", number: newPhone.trim() },
      ];
      await clientsApi.update(state.client._id, { phones: updatedPhones });
      setLocalClientPhones(updatedPhones);
      setShowPhoneForm(false);
      setNewPhone("");
    } catch (err: any) {
      setPhoneError(
        err.response?.data?.error || "Failed to save phone. Please try again."
      );
    } finally {
      setSavingPhone(false);
    }
  };

  // Issue & Send flow
  const handleIssueAndSend = async () => {
    setError(null);
    setStep("sending");
    try {
      // Step 1: Save
      const saved = await onSave();
      if (!saved) {
        setStep("review");
        setError("Failed to save the invoice. Please try again.");
        return;
      }

      // Step 2: Issue
      await invoicesApi.changeStatus(state.documentId, "issued");

      // Step 3: Send
      await invoicesApi.send(state.documentId);

      // Step 4: Success
      setStep("success");

      // Auto-close after brief delay
      setTimeout(() => {
        onComplete();
      }, 1800);
    } catch (err: any) {
      setStep("review");
      setError(
        err.response?.data?.error ||
          "Something went wrong. Please try again."
      );
    }
  };

  // Reset state when modal opens/closes
  const handleClose = () => {
    if (step === "sending") return; // Don't close while sending
    setStep("review");
    setError(null);
    setShowEmailForm(false);
    setShowPhoneForm(false);
    setNewEmail("");
    setNewPhone("");
    setEmailError("");
    setPhoneError("");
    onClose();
  };

  // Render success state
  if (step === "success") {
    return (
      <PremiumModal
        open={open}
        onClose={handleClose}
        title="Issue & Send Invoice"
        icon={Send}
        size="md"
      >
        <div className="flex flex-col items-center justify-center py-12">
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              type: "spring",
              stiffness: 260,
              damping: 20,
              delay: 0.1,
            }}
            className="h-20 w-20 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: "#0F513220" }}
          >
            <CheckCircle2
              className="h-10 w-10"
              style={{ color: "#0F5132" }}
            />
          </motion.div>
          <motion.h3
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl font-bold text-slate-800 mb-2"
          >
            Invoice Sent
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-sm text-slate-500 text-center"
          >
            {state.invoiceNumber} has been issued and sent to{" "}
            <span className="font-medium text-slate-700">
              {recipientEmail}
            </span>
          </motion.p>
        </div>
      </PremiumModal>
    );
  }

  return (
    <PremiumModal
      open={open}
      onClose={handleClose}
      title="Issue & Send Invoice"
      icon={Send}
      size="md"
      footer={
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            disabled={step === "sending"}
            className="px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleIssueAndSend}
            disabled={!hasEmail || step === "sending"}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-bold disabled:opacity-50 transition-colors"
            style={{
              backgroundColor: hasEmail ? "var(--color-primary)" : "#94a3b8",
            }}
          >
            {step === "sending" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Issue & Send
              </>
            )}
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Error banner */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="flex items-start gap-3 p-3 rounded-xl bg-red-50 border border-red-200"
            >
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Invoice Summary Card */}
        <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <FileText className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Invoice</p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {state.invoiceNumber || "Draft"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Client</p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {state.client?.displayName || "Unknown"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <Briefcase className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Matter</p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {state.matter?.title || "General"}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-white border border-slate-200 flex items-center justify-center flex-shrink-0">
                <Calendar className="h-4 w-4 text-slate-500" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-slate-500">Due Date</p>
                <p className="text-sm font-semibold text-slate-800 truncate">
                  {formatDate(state.dueAt)}
                </p>
              </div>
            </div>
          </div>

          {/* Total Amount */}
          <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-500">
              Total Amount
            </span>
            <span
              className="text-2xl font-bold"
              style={{ color: "var(--color-primary)" }}
            >
              {formatCurrency(state.totals.totalCents)}
            </span>
          </div>
          {state.totals.balanceDueCents !== state.totals.totalCents && (
            <div className="flex items-center justify-between mt-1">
              <span className="text-xs text-slate-400">Balance Due</span>
              <span className="text-sm font-semibold text-slate-700">
                {formatCurrency(state.totals.balanceDueCents)}
              </span>
            </div>
          )}
        </div>

        {/* Recipient Section */}
        <div>
          <h4 className="text-sm font-semibold text-slate-800 mb-3">
            Send To
          </h4>

          {/* Email */}
          {hasEmail ? (
            <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#0F513215" }}
              >
                <Mail className="h-4 w-4" style={{ color: "#0F5132" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {recipientEmail}
                </p>
                <p className="text-xs text-slate-400">Primary email</p>
              </div>
              <CheckCircle2
                className="h-5 w-5 flex-shrink-0"
                style={{ color: "#198754" }}
              />
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-amber-800">
                    No email address on file
                  </p>
                  <p className="text-xs text-amber-600 mt-0.5">
                    An email address is required to send this invoice.
                  </p>
                  {!showEmailForm && (
                    <button
                      type="button"
                      onClick={() => setShowEmailForm(true)}
                      className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-amber-700 hover:text-amber-900 transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add email address
                    </button>
                  )}
                </div>
              </div>

              {/* Inline email form */}
              <AnimatePresence>
                {showEmailForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-amber-200"
                  >
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => {
                            setNewEmail(e.target.value);
                            if (emailError) setEmailError("");
                          }}
                          placeholder="client@example.com"
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 ${
                            emailError
                              ? "border-red-300 bg-red-50"
                              : "border-amber-200 bg-white"
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSaveEmail();
                          }}
                          autoFocus
                        />
                        {emailError && (
                          <p className="text-xs text-red-600 mt-1">
                            {emailError}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleSaveEmail}
                        disabled={savingEmail}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        {savingEmail ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        Save & Continue
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Phone (optional) */}
          {!hasPhone && (
            <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/50 p-3">
              <div className="flex items-start gap-3">
                <Phone className="h-4 w-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs text-slate-500">
                    No phone number on file{" "}
                    <span className="text-slate-400">(optional)</span>
                  </p>
                  {!showPhoneForm && (
                    <button
                      type="button"
                      onClick={() => setShowPhoneForm(true)}
                      className="inline-flex items-center gap-1 mt-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                    >
                      <Plus className="h-3 w-3" />
                      Add phone
                    </button>
                  )}
                </div>
              </div>

              {/* Inline phone form */}
              <AnimatePresence>
                {showPhoneForm && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-3 pt-3 border-t border-slate-200"
                  >
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <input
                          type="tel"
                          value={newPhone}
                          onChange={(e) => {
                            setNewPhone(e.target.value);
                            if (phoneError) setPhoneError("");
                          }}
                          placeholder="(555) 123-4567"
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 ${
                            phoneError
                              ? "border-red-300 bg-red-50"
                              : "border-slate-200 bg-white"
                          }`}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleSavePhone();
                          }}
                          autoFocus
                        />
                        {phoneError && (
                          <p className="text-xs text-red-600 mt-1">
                            {phoneError}
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleSavePhone}
                        disabled={savingPhone}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-semibold disabled:opacity-50 transition-colors whitespace-nowrap"
                        style={{ backgroundColor: "var(--color-primary)" }}
                      >
                        {savingPhone ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        )}
                        Save
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}

          {/* Phone display when it exists */}
          {hasPhone && (
            <div className="mt-3 flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
              <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                <Phone className="h-4 w-4 text-slate-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-800 truncate">
                  {recipientPhone}
                </p>
                <p className="text-xs text-slate-400">Phone</p>
              </div>
            </div>
          )}
        </div>

        {/* Payment Link Preview */}
        {paymentUrl && (
          <div>
            <h4 className="text-sm font-semibold text-slate-800 mb-3">
              Payment Link
            </h4>
            <div className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 bg-white">
              <div
                className="h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: "#0F513215" }}
              >
                <Link2 className="h-4 w-4" style={{ color: "#0F5132" }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-slate-500 mb-0.5">
                  Included in email
                </p>
                <p className="text-xs font-mono text-slate-400 truncate">
                  {paymentUrl}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </PremiumModal>
  );
};

export default IssueAndSendModal;
